<template>
	<div
		class="chat-dialog"
		@click="onOpen"
	>
		<div class="chat-dialog__logo">
			<img
				v-if="props.dialog.PhotoLink"
				v-img-fade="props.dialog.PhotoLink"
				class="chat-dialog__logo-img"
				alt="Avatar"
			/>
			<div
				v-else
				:class="{
					'chat-dialog__logo-text': true,
					'chat-dialog__logo-text--personal': props.dialog.IsPersonal
				}"
			>
				{{ props.dialog.PhotoLiter }}
			</div>
		</div>
		<div class="chat-dialog__body">
			<div class="chat-dialog__body-header">
				<div class="chat-dialog__body-header-title">
					{{ props.dialog.Name }}
				</div>
				<div class="chat-dialog__body-header-datetime">
					{{ dialogDate }}
					<app-resizable-dropdown
						ref="$deleteDialog"
						name="$deleteDialog"
						position="left"
						:max-width="235"
						close-by-click-outside
					>
						<template #activator>
							<app-tooltip
								class="chat-dialog__body-header-tooltip"
								text="Удалить"
								position="right"
							>
								<template #activator>
									<app-icon
										v-if="!props.fromSearch"
										class="chat-dialog__body-header-btn"
										name="close-bold"
										size="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<dialog-delete-confirm
							:type="props.dialog.IsPersonal ? 'chat' : 'group'"
							:name="props.dialog.Name"
							@confirm="onDeleteDialog"
							@cancel="$deleteDialog.close()"
						/>
					</app-resizable-dropdown>
				</div>
			</div>
			<div :class="{
				'chat-dialog__body-message': true,
				'chat-dialog__body-message--unreaded': props.dialog.Unreaded && !props.dialog.UnreadCount,
			}"
			>
				<template v-if="props.dialog.ShowSmallPhoto">
					<img
						v-if="props.dialog.SmallPhotoLink"
						v-img-fade="props.dialog.SmallPhotoLink"
						class="chat-dialog__body-message-logo"
						alt="Avatar"
					/>
					<div
						v-else
						:class="{
							'chat-dialog__body-message-text-logo': true,
							'chat-dialog__body-message-text-logo--personal': props.dialog.IsPersonal
						}"
					>
						{{ props.dialog.SmallPhotoLiter }}
					</div>
				</template>
				<div
					:class="{
						'chat-dialog__body-message-text': true
					}"
					v-html="
						props.fromSearch
						? highlightSearchText(props.fromSearch, props.dialog.Message)
						: props.dialog.Message.split('\n')[0]
					"
				/>
				<div
					v-if="props.dialog.UnreadCount"
					class="chat-dialog__body-message-unreaded-count"
				>
					{{ props.dialog.UnreadCount }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import DialogDeleteConfirm from "@/components/main/chat-panel/dropdowns/DialogDeleteConfirm.vue";
import moment from "moment/moment";
import { Dialog } from "@/store/chats";
import { highlightSearchText } from "@/utils/textHelpers";

type ChatDialogProps = {
	dialog: Dialog,
	fromSearch?: string
}

const props = defineProps<ChatDialogProps>()

const emit = defineEmits<{
	(e: 'on-delete', value: number): void,
	(e: 'on-open', value: Dialog): void,
}>()

const $deleteDialog = ref<typeof AppResizableDropdown | null>(null)

const dialogDate = moment(props.dialog.MessageDate).format("DD.MM.YYYY HH:mm");

const onDeleteDialog = () => emit("on-delete", props.dialog.Id);
const onOpen = (event: MouseEvent) => {
	if ((event?.target as HTMLElement)?.closest('.link-message')) {
		return
	}
	emit("on-open", props.dialog)
};
</script>

<style scoped lang="scss">
.chat-dialog {
	display: flex;
	align-items: center;
	color: #4D4D4D;
	cursor: pointer;
	transition: all 0.1s ease-in;

	&:hover {
		.chat-dialog__body {
			background-color: #F2F2F2;
		}

		.chat-dialog__body-header-btn {
			opacity: 1;
		}
	}

	&__logo {
		display: flex;
		align-items: center;
		margin-right: 12px;
		height: 40px;
		width: 40px;

		&-img,
		&-text {
			height: 40px;
			width: 40px;
			border-radius: 50%;
		}

		&-text {
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 24px;
			text-transform: uppercase;
			color: #fff;
			background-color: #567CA3;

			&--personal {
				background-color: #E6C15A;
			}
		}
	}

	&__body {
		display: flex;
		flex-direction: column;
		padding: 2px 0;
		border-radius: 8px;
		width: 100%;
		min-height: 54px;

		&-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			position: relative;
			width: 100%;
			margin-bottom: 8px;
			padding: 0 4px;

			&-title {
				font-size: 14px;
				line-height: 18px;
				font-weight: 500;
				color: #4D4D4D;
			}

			&-datetime {
				display: flex;
				align-items: center;
				gap: 4px;
				font-size: 12px;
				line-height: 16px;
				color: #8C8C8C;
				white-space: nowrap;
			}

			&-btn {
				color: #999595;
				opacity: 0;

				&:hover {
					color: #499259;
				}
			}
		}

		&-message {
			display: flex;
			align-items: center;
			position: relative;
			height: 100%;
			border-radius: 8px;
			margin-left: 2px;
			width: calc(100% - 4px);

			&--unreaded {
				background-color: #F2F2F2;
			}

			&-logo {
				height: 24px;
				min-width: 24px;
				max-width: 24px;
				border-radius: 50%;
				margin-right: 6px;
			}

			&-text-logo {
				display: flex;
				align-items: center;
				justify-content: center;
				height: 24px;
				min-width: 24px;
				max-width: 24px;
				border-radius: 50%;
				margin-right: 6px;
				color: #fff;
				font-size: 14px;
				line-height: 24px;
				background-color: #567CA3;

				&--personal {
					background-color: #E6C15A;
				}
			}

			&-text {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 1;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: pre-wrap;
				font-size: 14px;
				line-height: 18px;
				border-radius: 5px;
				margin-right: 15px;
				padding-right: 30px;
				padding-left: 2px;
				color: #4D4D4D;
			}

			&-unreaded-count {
				display: flex;
				align-items: center;
				justify-content: center;
				position: absolute;
				right: 12px;
				min-width: 16px;
				height: 16px;
				background-color: #3F884F;
				border-radius: 8px;
				padding: 4px;
				color: #FFFFFF;
				font-size: 12px;
				line-height: 16px;
				font-weight: 500;
				box-shadow: 0 0 4px 0 #33333340;
			}
		}
	}

	&::v-deep(.dropdown-activator) {
		margin-top: -2px;
	}
}
</style>
